// src/scripts/sqlApiHelper/venuesApi.js

import { getAuth } from "firebase/auth";
import {
  doc,
  getDoc,
  getFirestore,
} from "firebase/firestore";

import { fsListAllVenues } from "@/scripts/firestore/venuesFs";
import {
  callEdge,
  getEdgeClaims,
} from "@/scripts/sqlApiHelper/edgeApi";

function normaliseVenueList(raw = []) {
  return (Array.isArray(raw) ? raw : [])
    .map((venue) => {
      const id = String(
        venue?.id ??
          venue?.venue_id ??
          ""
      ).trim();

      const label = String(
        venue?.venue_name ??
          venue?.venueName ??
          venue?.label ??
          venue?.name ??
          id
      ).trim();

      return {
        id,
        label,
      };
    })
    .filter((venue) => Boolean(venue.id))
    .sort((a, b) =>
      String(a.label || a.id).localeCompare(
        String(b.label || b.id),
        "en"
      )
    );
}

function isTrue(value) {
  return (
    value === true ||
    String(value || "").toLowerCase() === "true"
  );
}

function isSocialMediaProfile(profile = {}) {
  const userType = String(
    profile.user_type ??
      profile.userType ??
      ""
  )
    .trim()
    .toLowerCase();

  const role = String(profile.role || "")
    .trim()
    .toLowerCase();

  return (
    isTrue(profile.isSocialMedia) ||
    isTrue(profile.is_social_media) ||
    userType === "social_media" ||
    userType === "social-media" ||
    role === "social_media" ||
    role === "social-media"
  );
}

async function currentUserIsSocialMedia() {
  const user = getAuth().currentUser;

  if (!user?.uid) {
    return false;
  }

  try {
    const profileSnap = await getDoc(
      doc(
        getFirestore(),
        "users",
        user.uid
      )
    );

    if (!profileSnap.exists()) {
      return false;
    }

    return isSocialMediaProfile(
      profileSnap.data() || {}
    );
  } catch (error) {
    console.error(
      "[venuesApi] Failed to check Social Media user profile:",
      error
    );

    return false;
  }
}

/**
 * Admin/Operations: returns ALL venues from Edge.
 * Kept for screens that still use the Edge-backed venue list.
 */
export async function adminListVenues() {
  const res = await callEdge(
    "admin-venues-list",
    {}
  );

  if (!res?.ok) {
    throw new Error(
      res?.error ||
        "admin-venues-list failed"
    );
  }

  return Array.isArray(res.venues)
    ? res.venues
    : [];
}

export async function adminUpsertVenue({
  venue_id,
  venue_name,
}) {
  const res = await callEdge(
    "admin-venues-upsert",
    {
      venue_id,
      venue_name,
    }
  );

  if (!res?.ok) {
    throw new Error(
      res?.error ||
        "admin-venues-upsert failed"
    );
  }

  return res;
}

export async function adminDeleteVenues(
  venue_ids
) {
  const res = await callEdge(
    "admin-venues-delete",
    {
      venue_ids,
    }
  );

  if (!res?.ok) {
    throw new Error(
      res?.error ||
        "admin-venues-delete failed"
    );
  }

  return res;
}

/**
 * Returns venue IDs for the current user.
 *
 * Privileged users use admin-venues-list.
 * Standard users use venues-my-list.
 */
export async function listVenuesForPicker() {
  const claims = await getEdgeClaims();

  const isPrivileged =
    claims?.admin === true ||
    claims?.operations === true ||
    claims?.ops === true ||
    claims?.is_admin === true ||
    claims?.is_operations === true;

  const fnName = isPrivileged
    ? "admin-venues-list"
    : "venues-my-list";

  const res = await callEdge(
    fnName,
    {}
  );

  if (!res?.ok) {
    throw new Error(
      res?.error ||
        `Failed to load venues (${fnName})`
    );
  }

  const raw = Array.isArray(res.venues)
    ? res.venues
    : [];

  return raw
    .map((venue) =>
      typeof venue === "string"
        ? venue
        : (
            venue.venue_id ??
            venue.id ??
            ""
          )
    )
    .map((venueId) =>
      String(venueId || "").trim()
    )
    .filter(Boolean);
}

/**
 * Loads specifically assigned venue documents
 * for a multi-venue user.
 */
async function getAllowedVenueDocuments(
  venueIds = []
) {
  const ids = (
    Array.isArray(venueIds)
      ? venueIds
      : []
  )
    .map((venueId) =>
      String(venueId || "").trim()
    )
    .filter(Boolean);

  if (!ids.length) {
    return [];
  }

  const db = getFirestore();

  const results = await Promise.all(
    ids.map(async (venueId) => {
      try {
        const venueSnap = await getDoc(
          doc(
            db,
            "venues",
            venueId
          )
        );

        if (!venueSnap.exists()) {
          console.warn(
            `[venuesApi] Allowed venue does not exist: ${venueId}`
          );

          return null;
        }

        const data =
          venueSnap.data() || {};

        if (data.is_archived === true) {
          return null;
        }

        const id = String(
          data.venue_id ??
            venueSnap.id ??
            venueId
        ).trim();

        const label = String(
          data.venue_name ??
            data.venueName ??
            data.label ??
            data.name ??
            id
        ).trim();

        return {
          id,
          label,
        };
      } catch (error) {
        console.error(
          `[venuesApi] Failed to load permitted venue ${venueId}:`,
          error
        );

        return null;
      }
    })
  );

  return results
    .filter(Boolean)
    .sort((a, b) =>
      String(
        a.label ||
          a.id
      ).localeCompare(
        String(
          b.label ||
            b.id
        ),
        "en"
      )
    );
}

/**
 * VenueSnapshot dropdown helper.
 *
 * Operations/Admin:
 *   Returns every active venue.
 *
 * Social Media:
 *   Returns every active venue because this role manages
 *   artwork across the whole business.
 *
 * Multi-venue users:
 *   Returns only specifically assigned venue documents.
 *
 * Single users:
 *   Keeps the existing Edge-backed behaviour.
 */
export async function listVenuesForSnapshotDropdown({
  isOperations = false,
  isSocialMedia = false,
  userType = "single",
  allowedVenueIds = [],
} = {}) {
  const normalisedUserType = String(
    userType || ""
  )
    .trim()
    .toLowerCase();

  /*
   * The explicit property is checked first.
   * The current Firestore user profile is also checked so
   * existing callers do not need to be changed immediately.
   */
  const socialMediaAccess =
    isSocialMedia === true ||
    normalisedUserType === "social_media" ||
    normalisedUserType === "social-media" ||
    (await currentUserIsSocialMedia());

  /*
   * Operations and Social Media both require the complete
   * venue list.
   *
   * Social Media receives the venue names for the Artwork
   * workflow only. The navbar and Firebase rules continue
   * to restrict access to other screens and data.
   */
  if (
    isOperations ||
    socialMediaAccess
  ) {
    const raw =
      await fsListAllVenues();

    return normaliseVenueList(raw);
  }

  /*
   * Ordinary multi-venue users still receive only the
   * venues assigned to them.
   */
  if (normalisedUserType === "multi") {
    const allowed = (
      Array.isArray(allowedVenueIds)
        ? allowedVenueIds
        : []
    )
      .map((venueId) =>
        String(
          venueId || ""
        ).trim()
      )
      .filter(Boolean);

    if (!allowed.length) {
      console.warn(
        "[venuesApi] Multi-venue user has no allowed venue IDs."
      );

      return [];
    }

    return getAllowedVenueDocuments(
      allowed
    );
  }

  /*
   * Single-venue and other users retain the existing
   * Edge-backed behaviour.
   */
  const ids =
    await listVenuesForPicker();

  return ids.map((id) => ({
    id,
    label: id,
  }));
}